import React from "react";
import { Paper, Grid } from "@mui/material";
import { compose } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import { ButtonCompo } from "../../components/Comman/Button";
import DynamicDetails from "../../components/dynamicscreens/DynamicDetails";
import DynamicFormWithoutSave from "../../components/dynamicscreens/DynamicFormWithoutSave";
import LandingScreenHeader from "../../components/Comman/LandingScreenHeader";
import { ConvertAmount } from "../../components/Comman/ConvertValueWithDenominator";
import { withRouter } from "../../components/withRouter";
import { apiGet, apiPost } from "../../utils/api_service";
import { showLoador, showNotification } from "../Landing/Landing.slice";
import endpoint from "../../config/endpoints";
import {
	noInternetMsg,
	saveFailedMsg,
	savemsg,
	saveWarningMsg,
} from "../../config/messageconstant";
import { stockHolderTargetJson } from "../../DynamicFormsJson/Transaction/stockholderTarget";

// ── Sub-components ─────────────────────────────────────────────────────────────
import BeatTargetTable from "./components/BeatTargetTable";
import {
	DEFAULT_ROWS_PER_PAGE,
	MONTH_COLUMNS,
} from "./constants/stockHolderTargetConstants";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derives the three header fields (Year, FromDate, ToDate) from the raw API item.
 */
const buildHeaderData = (firstItem) => {
	return [
		{
			xs: 12,
			sm: 6,
			md: 6,
			lg: 6,
			label: "Year Name",
			value: firstItem.yearName ?? "-",
		},
		{
			xs: 12,
			sm: 6,
			md: 6,
			lg: 6,
			label: "From Date",
			value: firstItem.yearFromDate ?? "-",
		},
		{
			xs: 12,
			sm: 6,
			md: 6,
			lg: 6,
			label: "To Date",
			value: firstItem.yearToDate ?? "-",
		},
	];
};

/**
 * Maps a raw API item to the flat shape used in state / table rows.
 */
const toRowShape = (item, index) => ({
	index: index + 1,
	id: item.id,
	beatId: item.beatId,
	beatName: item.beatName,
	regionName: item.regionName,
	stateName: item.stateName,
	zoneName: item.zoneName,
	areaName: item.areaName,
	jan: item.jan,
	feb: item.feb,
	march: item.march,
	april: item.april,
	may: item.may,
	jun: item.jun,
	july: item.july,
	aug: item.aug,
	sep: item.sep,
	oct: item.oct,
	nov: item.nov,
	dec: item.dec,
	totalTarget: item.totalTarget,
});

/**
 * Sums `totalTarget` across every row in the list.
 */
const sumTotalTarget = (list) =>
	list.reduce((acc, row) => acc + (row.totalTarget ?? 0), 0);

/**
 * Returns rows whose monthly targets differ from the original loaded values.
 * Used so we only save / log the beats the user actually changed.
 */
const getChangedBeatRows = (currentList, originalList) => {
	const originalById = new Map(originalList.map((row) => [row.id, row]));

	return currentList.filter((row) => {
		const original = originalById.get(row.id);
		if (!original) return false;

		return MONTH_COLUMNS.some(
			({ name }) => String(row[name] ?? "") !== String(original[name] ?? ""),
		);
	});
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

class StockHolderTargetEdit extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			searchValue: "",
			DataToSet: [],
			beatTargetList: [],
			originalBeatTargetList: [],
			totalOftarget: 0,
			moduleObjDataObj: {},
			// Pagination (0-indexed, as required by MUI TablePagination)
			page: 0,
			rowsPerPage: DEFAULT_ROWS_PER_PAGE,
		};
	}

	// ── Lifecycle ──────────────────────────────────────────────────────────────

	async componentDidMount() {
		const { showLoador, showNotification } = this.props;

		if (!navigator.onLine) {
			showNotification({ msg: noInternetMsg, severity: "error" });
			return;
		}

		const moduleObjData = localStorage.getItem("moduleObj");
		this.setState({
			moduleObjDataObj: moduleObjData ? JSON.parse(moduleObjData) : {},
		});

		if (!this.props.params.id) return;

		showLoador({ loador: true });
		try {
			const response = await apiGet({
				url: `${stockHolderTargetJson.apiBaseURL}/list-by-year-optimize?yearId=${this.props.params.id}`,
			});
			showLoador({ loador: false });

			const { data, success } = response;
			if (!success) return;

			const beatTargetList = data.data.map(toRowShape);

			this.setState({
				DataToSet: buildHeaderData(data.data[0]),
				beatTargetList,
				originalBeatTargetList: beatTargetList.map((row) => ({ ...row })),
				totalOftarget: sumTotalTarget(beatTargetList),
			});
		} catch (_err) {
			showLoador({ loador: false });
		}
	}

	// ── Search / Filter ────────────────────────────────────────────────────────

	onSearch = (searchValue) => {
		this.setState({ searchValue, page: 0 });
	};

	/**
	 * Returns rows matching the current search term.
	 * Short-circuits when the search box is empty (no iteration needed).
	 */
	getFilteredTableData = () => {
		const { beatTargetList, searchValue } = this.state;
		if (!searchValue.trim()) return beatTargetList;

		const term = searchValue.trim().toLowerCase();
		return beatTargetList.filter(
			(row) =>
				row.beatName?.toLowerCase().includes(term) ||
				row.areaName?.toLowerCase().includes(term) ||
				row.zoneName?.toLowerCase().includes(term) ||
				row.stateName?.toLowerCase().includes(term) ||
				row.regionName?.toLowerCase().includes(term),
		);
	};

	// ── Table input change (unchanged logic) ───────────────────────────────────

	onTextFieldDataChange = (monthName, beatId) => (event) => {
		const { beatTargetList } = this.state;
		const { value } = event.target;

		const updatedBeatList = beatTargetList.map((beat) => {
			if (beat.id !== beatId) return beat;
			const oldValue = beat[monthName] || 0;
			const updated = { ...beat, [monthName]: value };
			updated.totalTarget = +updated.totalTarget - +oldValue + +value;
			return updated;
		});

		this.setState({
			beatTargetList: updatedBeatList,
			totalOftarget: sumTotalTarget(updatedBeatList),
		});
	};

	// ── Pagination ─────────────────────────────────────────────────────────────

	handlePageChange = (_event, newPage) => {
		this.setState({ page: newPage });
	};

	handleChangeRowsPerPage = (event) => {
		this.setState({
			rowsPerPage: parseInt(event.target.value, 10),
			page: 0,
		});
	};

	// ── Submit / Save (unchanged logic) ───────────────────────────────────────

	onSubmit = () => {
		const { beatTargetList } = this.state;

		const saveTargetList = beatTargetList.map((row) => ({
			...row,
			beat: row.beatId,
			yearId: this.props.params.id,
		}));

		swal({
			title: "Are you sure?",
			text: saveWarningMsg,
			icon: "warning",
			buttons: true,
			dangerMode: true,
		}).then((willDelete) => {
			if (willDelete) this.onSave(saveTargetList);
		});
	};

	onSave = async (saveTargetList) => {
		const { showLoador, showNotification } = this.props;
		const { moduleObjDataObj, originalBeatTargetList } = this.state;

		if (!navigator.onLine) {
			showNotification({ msg: noInternetMsg, severity: "error" });
			return;
		}

		const changedBeatTargets = getChangedBeatRows(
			saveTargetList,
			originalBeatTargetList,
		);
		const changedBeatIds = changedBeatTargets.map((row) => row.beatId);

		const dataToSaveForMLog = {
			module: { moduleId: moduleObjDataObj.moduleId },
			subModule: { subModuleId: moduleObjDataObj.subModuleId },
			operationType: 1,
			type: 1,
			operationPerformId: this.props.params.id,
			subOperationPerformIds: changedBeatIds.join(", "),
		};

		const saveDataJson = {
			beatTargets: changedBeatTargets,
			mlog: dataToSaveForMLog,
		};

		showLoador({ loador: true });
		await apiPost({
			url: `${endpoint.beatTarget}/save-list-with-logs`,
			postBody: saveDataJson,
		}).then(({ data, success }) => {
			if (!success) {
				showNotification({ msg: saveFailedMsg, severity: "error" });
				return;
			}

			showNotification({ msg: savemsg });
			this.props.navigate(-1);
		});
		showLoador({ loador: false });
	};

	// ── Render ─────────────────────────────────────────────────────────────────

	render() {
		const {
			DataToSet,
			totalOftarget,
			page,
			rowsPerPage,
			beatTargetList,
			originalBeatTargetList,
		} = this.state;

		const filteredData = this.getFilteredTableData();
		const pagedData = filteredData.slice(
			page * rowsPerPage,
			page * rowsPerPage + rowsPerPage,
		);
		const hasTargetChanges =
			getChangedBeatRows(beatTargetList, originalBeatTargetList).length > 0;

		return (
			<Paper sx={{ p: 2, borderRadius: 0 }} elevation={0}>
				{/* ── Header / Year details form ── */}
				<DynamicFormWithoutSave
					showBackToList={true}
					DataToSet={DataToSet}
					showSaveBtn={stockHolderTargetJson.showSaveBtn}
					showTitle={true}
					screenTitle="Stock Holder Target Assignation"
					fieldMeta={[]}
					showCancel={stockHolderTargetJson.showCancel}
					apiBaseURL={stockHolderTargetJson.apiBaseURL}
					showSaveNextBtn={stockHolderTargetJson.showSaveNextBtn}
					showSaveBtnMain={false}
					padding={true}
					paddingTop={true}
				/>

				{/* ── Search bar ── */}
				<LandingScreenHeader
					screenTitle=""
					showSearchBox={true}
					showTable={true}
					showFilter={false}
					showPdfDownload={false}
					showExcelDownload={false}
					onSearch={this.onSearch}
				/>

				{/* ── Table + Pagination ── */}
				<BeatTargetTable
					pagedData={pagedData}
					filteredCount={filteredData.length}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={this.handlePageChange}
					onRowsPerPageChange={this.handleChangeRowsPerPage}
					onTextFieldDataChange={this.onTextFieldDataChange}
				/>

				<br />

				{/* ── Grand total ── */}
				<DynamicDetails
					DataToSet={[
						{
							xs: 12,
							sm: 6,
							md: 6,
							lg: 6,
							label: "Total target",
							value: ConvertAmount({ amount: totalOftarget }),
						},
					]}
				/>

				{/* ── Submit button (only when beat targets were changed) ── */}
				{hasTargetChanges && (
					<Grid container spacing={1}>
						<Grid
							item
							xs={12}
							sm={12}
							md={12}
							lg={12}
							container
							justifyContent="right"
							rowSpacing={2}
							columnSpacing={{ xs: 12, sm: 6, md: 1 }}
						>
							<ButtonCompo
								size="medium"
								type="submit"
								variant="contained"
								name="Submit"
								onClick={this.onSubmit}
							/>
						</Grid>
					</Grid>
				)}
			</Paper>
		);
	}
}

// ─────────────────────────────────────────────────────────────────────────────
// Redux wiring
// ─────────────────────────────────────────────────────────────────────────────

const mapStateToProps = (state) => ({
	beatTargetList: state.beatTarget,
});

const mapDispatchToProps = { showNotification, showLoador };

export default compose(
	withRouter,
	connect(mapStateToProps, mapDispatchToProps),
)(StockHolderTargetEdit);